/*
 * Peel — shared helpers used across the homepage and collection pages.
 */
(function () {
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Scroll reveal: fade-up matched sections as they enter view ---- */
  window.peelInitScrollReveal = function (selectors) {
    var targets = document.querySelectorAll(selectors.join(','));
    if (!targets.length) return;

    targets.forEach(function (el) {
      el.setAttribute('data-peel-reveal', '');
    });

    if (reducedMotion || typeof IntersectionObserver === 'undefined') {
      targets.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -70px 0px', threshold: 0.05 }
    );
    targets.forEach(function (el) {
      observer.observe(el);
    });
  };

  /* ---- Horizontal rail: prev/next scroll, wraps at either end ---- */
  window.peelInitRail = function (railSelector, prevSelector, nextSelector) {
    var rail = document.querySelector(railSelector);
    if (!rail) return;
    var prev = document.querySelector(prevSelector);
    var next = document.querySelector(nextSelector);

    function step(dir) {
      var first = rail.firstElementChild;
      var gap = parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap) || 28;
      var amount = first ? first.getBoundingClientRect().width + gap : 340;
      var max = rail.scrollWidth - rail.clientWidth;
      if (max <= 0) return;
      var behavior = reducedMotion ? 'auto' : 'smooth';
      var atEnd = rail.scrollLeft >= max - 2;
      var atStart = rail.scrollLeft <= 2;

      if (dir > 0 && atEnd) {
        rail.scrollTo({ left: 0, behavior: behavior });
        return;
      }
      if (dir < 0 && atStart) {
        rail.scrollTo({ left: max, behavior: behavior });
        return;
      }

      var target = Math.max(0, Math.min(max, rail.scrollLeft + dir * amount * 2));
      rail.scrollTo({ left: target, behavior: behavior });
    }

    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(1); });
  };

  /* ---- Device picker: cascading brand -> model selects ----
     Both the homepage finder and the collection hero read the same
     catalogue, which peel-device-map.js builds from the compatibility the
     products declare. The shopper's phone is remembered, so picking it on
     the homepage carries through to the collection they land on. */
  var DEVICE_STORAGE_KEY = 'peel:device';

  window.peelDeviceSelection = {
    get: function () {
      try {
        var raw = window.localStorage.getItem(DEVICE_STORAGE_KEY);
        var parsed = raw ? JSON.parse(raw) : null;
        return parsed && parsed.key ? parsed : null;
      } catch (error) {
        return null;
      }
    },
    set: function (device) {
      try {
        if (device && device.key) {
          window.localStorage.setItem(DEVICE_STORAGE_KEY, JSON.stringify(device));
        } else {
          window.localStorage.removeItem(DEVICE_STORAGE_KEY);
        }
      } catch (error) {
        /* Private browsing — the picker still works, it just forgets. */
      }
    }
  };

  /*
   * options:
   *   includeAny   add an "All models" choice, for the collection filter
   *   anyLabel     what that choice reads
   *   initialKey   device key to start on ('' forces "All models")
   *   initialBrand brand to open on when no phone is chosen — a
   *                collection of one brand's phones opens on that brand
   *                rather than on the first in the list
   *   selectFirst  pick the newest model when nothing else applies
   *   onChange     fn(device|null, { initial: bool })
   */
  window.peelInitDeviceSelects = function (brandSelect, modelSelect, options) {
    options = options || {};
    if (!brandSelect || !modelSelect || !window.peelDeviceMap) return null;

    var catalog = window.peelDeviceMap.catalog();
    var picker = brandSelect.closest('[data-peel-device-picker]');

    /* Nothing declared to list — an empty picker helps nobody. */
    if (!catalog.length) {
      if (picker) picker.hidden = true;
      return null;
    }
    if (picker) picker.hidden = false;

    function brandByName(name) {
      for (var i = 0; i < catalog.length; i++) {
        if (catalog[i].name === name) return catalog[i];
      }
      return catalog[0];
    }

    function brandHolding(key) {
      for (var i = 0; i < catalog.length; i++) {
        for (var j = 0; j < catalog[i].models.length; j++) {
          if (catalog[i].models[j].key === key) return catalog[i];
        }
      }
      return null;
    }

    function option(value, label) {
      var el = document.createElement('option');
      el.value = value;
      el.textContent = label;
      return el;
    }

    function populateModels(brand, key) {
      modelSelect.innerHTML = '';
      if (options.includeAny) {
        modelSelect.appendChild(option('', options.anyLabel || 'All models'));
      }
      brand.models.forEach(function (model) {
        modelSelect.appendChild(option(model.key, model.model));
      });
      var wanted = key && brandHolding(key) === brand ? key : null;
      if (!wanted && !options.includeAny) wanted = brand.models[0].key;
      modelSelect.value = wanted || '';
    }

    function current() {
      if (!modelSelect.value) return null;
      var model = window.peelDeviceMap.findModel(modelSelect.value);
      if (!model) return null;
      return { brand: model.brand, model: model.model, key: model.key };
    }

    function announce(initial) {
      var device = current();
      if (!initial) window.peelDeviceSelection.set(device);
      if (options.onChange) options.onChange(device, { initial: initial === true });
    }

    catalog.forEach(function (brand) {
      brandSelect.appendChild(option(brand.name, brand.name));
    });

    /* An explicit key wins over the remembered phone; '' means the caller
       wants the picker to open unfiltered. */
    var startKey = options.initialKey;
    if (startKey === undefined || startKey === null) {
      var stored = window.peelDeviceSelection.get();
      startKey = stored ? stored.key : null;
    }
    if (startKey && !brandHolding(startKey)) startKey = null;
    /* brandByName falls back to the first brand, so a brand the catalogue
       does not list — or none asked for — opens where it always has. */
    var openBrand = brandByName(options.initialBrand);
    if (!startKey && options.selectFirst !== false) startKey = openBrand.models[0].key;

    var startBrand = startKey ? brandHolding(startKey) : openBrand;
    brandSelect.value = startBrand.name;
    populateModels(startBrand, startKey);

    brandSelect.addEventListener('change', function () {
      populateModels(brandByName(brandSelect.value), null);
      announce(false);
    });
    modelSelect.addEventListener('change', function () {
      announce(false);
    });

    announce(true);

    return {
      current: current,
      /* Used when something outside the picker changes the device — the
         back button, or clearing the filter from the results. */
      select: function (key) {
        var brand = key ? brandHolding(key) : null;
        if (key && !brand) return;
        if (brand) brandSelect.value = brand.name;
        populateModels(brand || brandByName(brandSelect.value), key);
        announce(false);
      }
    };
  };
})();
